import { useProfile } from "@/contexts/ProfileContext";
import { Loader2, AlertCircle } from "lucide-react"; 
import { useState, useEffect, useCallback } from "react";
import { useUserTests } from "@/hooks/useUserTests";
import type { Test } from "@/components/job/types";
import { Navigate, useSearchParams } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { DataPagination } from "@/components/ui/data-pagination";
import { ShareTestDialog } from "@/components/job/ShareTestDialog";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { CollaborationInvitations } from "@/components/collaboration/CollaborationInvitations";
import { InviteCollaboratorDialog } from "@/components/collaboration/InviteCollaboratorDialog";
import { CreateJobButton } from "@/components/job/CreateJobButton";
import { MetricsDashboard } from "@/components/test-management/MetricsDashboard";
import { TestToolbar, FilterType } from "@/components/test-management/TestToolbar";
import { EnterpriseTestTable } from "@/components/test-management/EnterpriseTestTable";
import { BulkActions } from "@/components/test-management/BulkActions";
import { useTestFilters } from "@/hooks/useTestFilters";
import { MobileTestCard } from "@/components/job/MobileTestCard";
import { Card, CardContent } from "@/components/ui/card";
import { useDuplicateTest } from "@/hooks/useDuplicateTest";

export default function TestManagement() {
  const { profile } = useProfile();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [inviteCollabDialogOpen, setInviteCollabDialogOpen] = useState(false);
  const [selectedTestForCollab, setSelectedTestForCollab] = useState<string | null>(null);
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [visibleColumns, setVisibleColumns] = useState([
    "title", "responses", "location", "created", "active", "actions"
  ]);
  
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { subscription, isLoading: isLoadingSubscription, refreshSubscription } = useSubscription();
  const { duplicateTest } = useDuplicateTest();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1");
  
  const PAGE_SIZE = 10;

  const { tests, isLoading: isLoadingUserTests, error: userTestsError, deleteTest, refreshTests } = useUserTests();
  
  // Filter and search functionality
  const {
    searchQuery,
    activeFilters,
    filteredTests,
    metrics,
    handleSearchChange,
    handleFilterChange
  } = useTestFilters(tests || []);

  // Listen for test updates and refresh tests
  useEffect(() => {
    const handleTestsUpdated = () => {
      refreshTests();
    };
    
    window.addEventListener('testsUpdated', handleTestsUpdated);
    
    return () => {
      window.removeEventListener('testsUpdated', handleTestsUpdated);
    };
  }, [refreshTests]);


  const handleDeleteTest = async (testId: string) => {
    setIsDeleting(true);
    try {
      await deleteTest(testId);
      
      // Update subscription data after test deletion
      if (subscription) {
        try {
          // Directly call the function to update subscription usage
          await supabase.functions.invoke('direct-subscription-update', {
            body: { userId: profile?.id, testAction: 'delete' }
          });
        } catch (err) {
          console.error("Failed to update subscription after test deletion:", err);
          // Non-critical error, don't show to user
        }
      }
      
      toast({
        title: "Test deleted",
        description: "The test and all associated data have been deleted."
      });
    } catch (error) {
      console.error("Failed to delete test:", error);
      toast({
        title: "Error deleting test",
        description: "There was a problem deleting the test. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleInviteCandidates = (testId: string) => {
    setSelectedTestId(testId);
    setShareDialogOpen(true);
  };

  const handleInviteCollaborator = (testId: string) => {
    setSelectedTestForCollab(testId);
    setInviteCollabDialogOpen(true);
  };

  const handleDuplicateTest = async (testId: string) => {
    await duplicateTest(testId);
    refreshTests();
  };

  const handleToggleTestStatus = async (testId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('tests')
        .update({ is_active: !currentStatus })
        .eq('id', testId);

      if (error) throw error;
      
      await refreshSubscription();
      refreshTests();
      
      toast({
        title: currentStatus ? "Test deactivated" : "Test activated",
        description: currentStatus 
          ? "The test has been successfully deactivated." 
          : "The test has been successfully activated.",
      });
    } catch (error: any) {
      console.error('Error toggling test status:', error);
      toast({
        title: "Error",
        description: error.message || "There was a problem updating the test status.",
        variant: "destructive"
      });
    }
  };

  // Bulk actions
  const handleTestSelect = useCallback((testId: string, selected: boolean) => {
    setSelectedTests(prev => 
      selected 
        ? [...prev, testId]
        : prev.filter(id => id !== testId)
    );
  }, []);

  const totalPages = Math.max(1, Math.ceil((filteredTests?.length || 0) / PAGE_SIZE));
  
  const paginatedTests = filteredTests?.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSelectAll = useCallback((selected: boolean) => {
    setSelectedTests(selected ? paginatedTests?.map(test => test.id) || [] : []);
  }, [paginatedTests]);

  const handleClearSelection = useCallback(() => {
    setSelectedTests([]);
  }, []);

  const handleBulkDelete = useCallback(async (testIds: string[]) => {
    for (const testId of testIds) {
      await deleteTest(testId);
    }
    toast({
      title: "Tests deleted",
      description: `${testIds.length} test${testIds.length > 1 ? 's' : ''} deleted successfully.`,
    });
  }, [deleteTest, toast]);

  const handleBulkActivate = useCallback(async (testIds: string[]) => {
    try {
      const { error } = await supabase
        .from('tests')
        .update({ is_active: true })
        .in('id', testIds);

      if (error) throw error;
      
      await refreshSubscription();
      refreshTests();
      
      toast({
        title: "Tests activated",
        description: `${testIds.length} test${testIds.length > 1 ? 's' : ''} activated successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to activate tests.",
        variant: "destructive"
      });
    }
  }, [refreshSubscription, refreshTests, toast]);

  const handleBulkDeactivate = useCallback(async (testIds: string[]) => {
    try {
      const { error } = await supabase
        .from('tests')
        .update({ is_active: false })
        .in('id', testIds);

      if (error) throw error;
      
      await refreshSubscription();
      refreshTests();
      
      toast({
        title: "Tests deactivated", 
        description: `${testIds.length} test${testIds.length > 1 ? 's' : ''} deactivated successfully.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to deactivate tests.",
        variant: "destructive"
      });
    }
  }, [refreshSubscription, refreshTests, toast]);



  const handlePageChange = (page: number) => {
    setSearchParams({ page: page.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Safe early returns after all hooks are declared
  if (profile?.role !== 'employer') {
    console.log(`User role (${profile?.role}) is not employer. Redirecting from /test-management.`);
    return <Navigate to="/" replace />;
  }

  if (isLoadingUserTests || isLoadingSubscription) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)] bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (userTestsError) {
    console.error("Test loading error:", userTestsError);
    return (
      <div className="container py-6 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {userTestsError.message?.includes("policy")
              ? "There was a permissions issue loading your tests. Please try refreshing the page or contact support."
              : "There was a problem loading your tests. Please try refreshing the page or contact support."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background pb-20 md:pb-0">
      <div className="container max-w-7xl mx-auto py-8 px-6 md:px-8 lg:px-12 space-y-8">
        {/* Collaboration invitations */}
        <CollaborationInvitations />
        
        {/* Page header */}
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                Test Management
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              <CreateJobButton />
            </div>
          </div>

          {/* Metrics dashboard */}
          <MetricsDashboard {...metrics} />
        </div>

        {/* Main content card */}
        <Card className="bg-white">
          <CardContent className="p-6 space-y-6">
            {/* Toolbar */}
            <TestToolbar
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              visibleColumns={visibleColumns}
              onColumnVisibilityChange={setVisibleColumns}
              totalCount={tests?.length || 0}
              filteredCount={filteredTests?.length || 0}
            />

            {/* Bulk actions */}
            <BulkActions
              selectedTests={selectedTests}
              onClearSelection={handleClearSelection}
              onBulkDelete={handleBulkDelete}
              onBulkActivate={handleBulkActivate}
              onBulkDeactivate={handleBulkDeactivate}
            />

            {/* Content */}
            {isMobile ? (
              <div className="space-y-4">
                  {paginatedTests?.map((test) => (
                    <MobileTestCard 
                      key={test.id}
                      test={test as any}
                      onDeleteTest={handleDeleteTest}
                      onInviteCandidates={handleInviteCandidates}
                    />
                  )) || (
                  <div className="p-8 text-center">
                    <h3 className="text-xl font-semibold mb-2">No tests found</h3>
                    <p className="text-muted-foreground">
                      No tests match your current search and filter criteria.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <EnterpriseTestTable
                tests={paginatedTests as any || []}
                selectedTests={selectedTests}
                onTestSelect={handleTestSelect}
                onSelectAll={handleSelectAll}
                visibleColumns={visibleColumns}
                onDeleteTest={handleDeleteTest}
                onInviteCandidates={handleInviteCandidates}
                onToggleStatus={handleToggleTestStatus}
                onInviteCollaborator={handleInviteCollaborator}
                onDuplicateTest={handleDuplicateTest}
                isLoading={isLoadingUserTests}
              />
            )}
          </CardContent>
        </Card>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center">
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Dialogs */}
        {selectedTestId && (
          <ShareTestDialog
            open={shareDialogOpen}
            onOpenChange={setShareDialogOpen}
            testId={selectedTestId}
          />
        )}

        {selectedTestForCollab && (
          <InviteCollaboratorDialog
            open={inviteCollabDialogOpen}
            onOpenChange={setInviteCollabDialogOpen}
            testId={selectedTestForCollab}
          />
        )}
      </div>
    </div>
  );
}
